import { useQueries } from "@tanstack/react-query";
import { useShallow } from "zustand/react/shallow";

import { useGetTradingAccount, useRequest } from "~/v2/api/hooks";
import type { QueryOptions } from "~/v2/api/hooks/useRequest";
import {
  getWatchlist,
  getWatchlists,
  membershipSymbols,
  type Watchlist,
  watchlistsQueryKeys,
} from "~/v3-poc/api/watchlists";
import { useWorkspace } from "~/v3-poc/features/workspace/shared/workspace/Workspace.context";
import { watchlistSymbolKey } from "~/v3-poc/utils/watchlistSymbol";

export const useGetWatchlists = ({
  accountID,
  options = {},
}: {
  accountID?: string;
  options?: QueryOptions<Watchlist[]>;
}) => {
  // The key still needs a slot when the account is unresolved; `enabled`
  // keeps the disabled key from ever being fetched under `""`.
  const accountId = accountID ?? "";
  return useRequest(
    watchlistsQueryKeys.list(accountId),
    () => getWatchlists({ accountId }),
    { ...options, enabled: Boolean(accountID) && (options.enabled ?? true) },
  );
};

export const useGetWatchlist = ({
  accountID,
  watchlistId,
  options = {},
}: {
  accountID?: string;
  watchlistId?: string;
  options?: QueryOptions<Watchlist>;
}) => {
  const accountId = accountID ?? "";
  const listId = watchlistId ?? "";
  return useRequest(
    watchlistsQueryKeys.detail(accountId, listId),
    () => getWatchlist({ accountId, watchlistId: listId }),
    {
      ...options,
      enabled:
        Boolean(accountID) && Boolean(watchlistId) && (options.enabled ?? true),
    },
  );
};

/**
 * Union of every symbol across every workspace widget currently bound to a
 * watchlist, keyed via {@link watchlistSymbolKey} so a crypto pair (`BTC/USD`)
 * matches its unslashed position symbol (`BTCUSD`). Duplicate bindings to the
 * same watchlist share one fetch, and each query uses the very
 * `watchlistsQueryKeys.detail` key {@link useGetWatchlist} uses — so it shares
 * cache entries with every bound widget and stays fresh after an
 * add/rename invalidation rather than needing its own private key. A member
 * that fails to load (e.g. a stale 404) is skipped, not allowed to fail the
 * whole union.
 */
export const useBoundWatchlistSymbols = (): ReadonlySet<string> => {
  const boundWatchlistIds = useWorkspace(
    useShallow((state) =>
      [
        ...new Set(
          Object.values(state.widgets)
            .filter(
              (widget) => widget.kind === "watchlist" && widget.watchlistId,
            )
            .map((widget) => widget.watchlistId as string),
        ),
      ].sort(),
    ),
  );

  const { account } = useGetTradingAccount();
  const accountId = account?.id;

  return useQueries({
    queries: boundWatchlistIds.map((watchlistId) => ({
      queryKey: watchlistsQueryKeys.detail(accountId ?? "", watchlistId),
      queryFn: () =>
        getWatchlist({ accountId: accountId as string, watchlistId }),
      enabled: Boolean(accountId),
      meta: { showErrorToast: false },
    })),
    combine: (results): ReadonlySet<string> => {
      const symbols = new Set<string>();
      for (const result of results) {
        if (!result.data) {
          continue;
        }
        for (const symbol of membershipSymbols(result.data)) {
          symbols.add(watchlistSymbolKey(symbol));
        }
      }
      return symbols;
    },
  });
};
